import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Link,
  Stack,
  Button
} from "@mui/material";

const ExampleCode = ({ children }) => (
  <Paper variant="outlined" sx={{ p: 2, bgcolor: "#eef7ff", borderLeft: "6px solid", borderColor: "primary.main", mb: 2 }}>
    <Typography component="div" sx={{ fontWeight: 700, mb: 1 }}>{children.props.title}</Typography>
    <Box component="pre" sx={{ m: 0, p: 1, bgcolor: (theme) => theme.palette.grey[100], borderRadius: 1, overflowX: "auto" }}>
      <code>{children.props.code}</code>
    </Box>
  </Paper>
);

const KioskDoc = () => {
  const exampleLink = "https://iwaypluskiosk-c7f85.web.app/?venueId=YOUR_VENUE_NAME&sourceId=LOCATION_SOURCE_ID&destinationId=LOCATION_DESTINATION_ID&api_key=YOUR_API_KEY";
  const landmarkApi = "GET https://maps.iwayplus.in/secured/landmark-ids/YOUR_VENUE_NAME/view?api_key=YOUR_API_KEY";
  const urlTemplate = "https://iwaypluskiosk-c7f85.web.app/?venueId=YOUR_VENUE_NAME&sourceId=LOCATION_SOURCE_ID&destinationId=LOCATION_DESTINATION_ID&api_key=YOUR_API_KEY";

  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh", py: 6 }}>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ borderRadius: 3, height: "100vh", overflow: "scroll" }}>
          {/* <Box sx={{ bgcolor: "#0069d9", color: "#fff", p: 3, textAlign: "center" }}>
            <Typography variant="h4">IwayPlus Kiosk Integration Guide</Typography>
            <Typography sx={{ opacity: 0.9 }}>Documentation for embedding venue-based kiosk</Typography>
          </Box> */}

          <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Typography variant="h5" component="h2" gutterBottom>Overview</Typography>
            <Typography paragraph>
              This documentation explains how developers can integrate the iWayPlus Kiosk system into their web or mobile platform by using a parameterized URL. The map automatically renders the route between a specified source and destination inside a venue.
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "#eef7ff",
                borderLeft: "6px solid",
                borderColor: "primary.main",
                mb: 3,
                position: "relative"
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Example Link
              </Typography>

              {/* Copy Button */}
              <Box sx={{ position: "absolute", top: 12, right: 12 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => navigator.clipboard.writeText(exampleLink)}
                  sx={{ textTransform: "none", py: 0.3, px: 1.2 }}
                >
                  Copy
                </Button>
              </Box>

              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 2,
                  p: 1.5,
                  bgcolor: (theme) => theme.palette.grey[100],
                  borderRadius: 1,
                  overflowX: "auto"
                }}
              >
                <code>{exampleLink}</code>
              </Box>
            </Paper>

            <Typography variant="h6" gutterBottom>Parameters</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: (theme) => theme.palette.grey[100] }}>
                    <TableCell>Parameter</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Example</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><code>venueId</code></TableCell>
                    <TableCell>
                      The name of the venue for which the map will load. You can find this in your <strong>My Venues</strong> tab after logging into your Iway account.
                    </TableCell>
                    <TableCell><code>WolfLawLibrary</code></TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell><code>sourceId</code></TableCell>
                    <TableCell>
                      The unique identifier of the starting point (landmark) inside the venue. Obtainable from the Landmark ID API.
                    </TableCell>
                    <TableCell><code>68cbdbde7338440c85caf91f</code></TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell><code>destinationId</code></TableCell>
                    <TableCell>
                      The unique identifier of the destination (landmark) inside the venue. Also obtained from the Landmark ID API.
                    </TableCell>
                    <TableCell><code>68fb2ddba6cc47abfa305150</code></TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell><code>api_key</code></TableCell>
                    <TableCell>
                      Your personal or project-specific API key available under the <strong>API Keys</strong> tab in your Iway dashboard.
                    </TableCell>
                    <TableCell><code>37e25430</code></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h6" gutterBottom>Step-by-Step Integration</Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>Step 1 — Get the Venue ID</Typography>
            <Typography paragraph>
              Log in to your Iway account and navigate to the <strong>My Venues</strong> tab. Copy the <em>Venue Name</em> exactly as it appears — this will be your <code>venueId</code> value.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>Step 2 — Fetch Landmark IDs</Typography>
            <Typography paragraph>
              Use the following API endpoint to fetch all available landmarks (rooms, services, locations) within the selected venue.
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2
              }}
            >
              <Box
                component="pre"
                sx={{
                  flex: 1,
                  p: 2,
                  bgcolor: (theme) => theme.palette.grey[100],
                  borderRadius: 1,
                  overflowX: "auto",
                  m: 0
                }}
              >
                <code>{landmarkApi}</code>
              </Box>

              <Button
                size="small"
                variant="contained"
                onClick={() => navigator.clipboard.writeText(landmarkApi)}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Copy
              </Button>
            </Box>




            <Typography paragraph>
              The API returns a <strong>key-value map</strong> where the key is the landmark name and the value is its corresponding unique ID.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Example Response</Typography>
              <Box component="pre" sx={{ m: 0, p: 1, bgcolor: (theme) => theme.palette.grey[50], borderRadius: 1, overflowX: "auto" }}>
                {`{
  "Main Entrance": "68cbdbde7338440c85caf91f",
  "Penney Young Courtroom 275": "68fb2ddba6cc47abfa305150",
  "Restroom 1": "68cd1ad45efb47b0ba205200"
}`}
              </Box>
            </Paper>

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>Step 3 — Generate the Map URL</Typography>
            <Typography paragraph>Construct the URL by passing all four parameters:</Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2
              }}
            >
              <Box
                component="pre"
                sx={{
                  flex: 1,
                  p: 2,
                  bgcolor: (theme) => theme.palette.grey[100],
                  borderRadius: 1,
                  overflowX: "auto",
                  m: 0
                }}
              >
                <code>{urlTemplate}</code>
              </Box>

              <Button
                size="small"
                variant="contained"
                onClick={() => navigator.clipboard.writeText(urlTemplate)}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Copy
              </Button>
            </Box>


            {/* <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Example</Typography>
              <Box component="pre" sx={{ m: 0, p: 1, bgcolor: (theme) => theme.palette.grey[100], borderRadius: 1, overflowX: "auto" }}>
                <code>{exampleLink}</code>
              </Box>
            </Paper> */}

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1 }}>Step 4 — Embed the Map</Typography>
            <Typography paragraph>
              The map can be embedded directly inside an iframe or opened in a new browser tab.
            </Typography>

            <Box component="pre" sx={{ p: 2, bgcolor: (theme) => theme.palette.grey[100], borderRadius: 1, overflowX: "auto" }}>
              <code>{`<iframe
  src="${exampleLink}"
  width="100%"
  height="700"
  style="border:none; border-radius:10px;"
></iframe>`}</code>
            </Box>

            <Typography variant="h6" gutterBottom>Notes</Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>Ensure the <code>api_key</code> used has valid access rights to the selected venue.</li>
              <li>Each venue may have different sets of landmarks — always fetch the latest landmark list via API.</li>
              <li>If any parameter is missing or invalid, the map may not load or route correctly.</li>
              <li>The feature supports dynamic floor switching, accessible routing, and live preview playback.</li>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Stack spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">© 2025 Iway Plus Maps Integration Documentation</Typography>
            </Stack>

          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default KioskDoc
